/**
 * useBackHandler — let a transient overlay (image/PDF viewer, sheet, custom
 * modal) claim the Android hardware Back gesture so pressing Back CLOSES the
 * overlay instead of navigating the router back to the previous screen.
 *
 * Why a registry rather than a fake history entry: the app uses React Router's
 * data router (createBrowserRouter), which owns the History API. Pushing our own
 * entry to represent "modal open" fights that ownership and is brittle. Instead,
 * overlays register a close-handler on a small LIFO stack; the single Android
 * back-button listener (AndroidBackButton) consults the stack FIRST and, when a
 * handler is present, runs it and suppresses navigation.
 *
 * LIFO so stacked overlays close top-first. Nothing reads the stack on web/iOS,
 * so registering there is a harmless no-op — the reported issue is Android's
 * back gesture, which fires Capacitor's `backButton` event.
 */
import { useEffect, useRef } from "react";

type BackHandler = () => void;

const stack: BackHandler[] = [];

/** Register a back handler; returns a disposer that removes exactly it. */
export function pushBackHandler(handler: BackHandler): () => void {
  stack.push(handler);
  return () => {
    const i = stack.lastIndexOf(handler);
    if (i !== -1) stack.splice(i, 1);
  };
}

/** Run the top-most handler if any. Returns true when one handled the Back. */
export function runTopBackHandler(): boolean {
  const handler = stack[stack.length - 1];
  if (!handler) return false;
  handler();
  return true;
}

/**
 * Register `onBack` while `active` is true. The latest `onBack` is always used
 * (via a ref), so it can close over fresh state without re-registering on every
 * render; it only re-registers when `active` flips.
 */
export function useBackHandler(active: boolean, onBack: () => void): void {
  const ref = useRef(onBack);
  useEffect(() => { ref.current = onBack; });
  useEffect(() => {
    if (!active) return;
    return pushBackHandler(() => ref.current());
  }, [active]);
}
